<template>
  <div class="rise-in space-y-6">
    <!-- List View -->
    <div v-if="!selectedPatientId" class="card card-pad">
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
      >
        <div>
          <h1 class="page-title">Data Management</h1>
          <p class="page-subtitle">
            Browse and correct clinical data across all projects.
          </p>
        </div>
      </div>

      <!-- Filters & Search -->
      <div class="flex flex-col sm:flex-row gap-4 mb-6">
        <div class="flex-1 relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by Patient ID..."
            class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-brand-600 focus:ring focus:ring-brand-100 focus:outline-none text-sm text-ink transition-colors"
          />
          <font-awesome-icon
            icon="search"
            class="absolute left-3.5 top-3.5 text-gray-400 text-sm"
          />
        </div>

        <!-- Project Filter Pills -->
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-for="p in projects"
            :key="p"
            class="px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer"
            :class="
              selectedProject === p
                ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                : 'bg-brand-50 text-brand-700 border-transparent hover:bg-brand-100'
            "
            @click="selectedProject = p"
          >
            {{ p === 'all' ? 'All Projects' : p.toUpperCase() }}
          </button>
        </div>
      </div>

      <!-- Entry-status Filter Pills -->
      <div class="flex flex-wrap items-center gap-2 mb-6">
        <span class="text-xs font-semibold text-ink-muted mr-1">Status:</span>
        <button
          v-for="s in statusFilters"
          :key="s.key"
          class="px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer"
          :class="
            statusFilter === s.key
              ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
              : 'bg-brand-50 text-brand-700 border-transparent hover:bg-brand-100'
          "
          @click="statusFilter = s.key"
        >
          {{ s.label }}
        </button>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex items-center gap-2 text-ink-muted py-12 justify-center"
      >
        <font-awesome-icon icon="circle-notch" spin class="text-brand-600" />
        <span>Loading patients…</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-red-600 text-sm text-center py-6">
        {{ error }}
      </div>

      <!-- Patients Table -->
      <div v-else class="overflow-x-auto rounded-xl border border-gray-100">
        <table class="w-full text-left border-collapse text-sm">
          <thead>
            <tr
              class="bg-gray-50 border-b border-gray-100 text-ink-muted font-bold text-xs uppercase tracking-wider"
            >
              <th class="px-6 py-4">Patient ID</th>
              <th class="px-6 py-4">Project</th>
              <th class="px-6 py-4">Status</th>
              <th class="px-6 py-4">Created Date</th>
              <th class="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="p in filteredPatients"
              :key="p.patient_id"
              class="hover:bg-gray-50/50 transition-colors"
            >
              <td class="px-6 py-4 font-semibold text-ink">
                {{ p.patient_id }}
              </td>
              <td class="px-6 py-4">
                <span class="pill pill-brand">{{
                  (p.projects && p.projects.name) || p.project_id
                }}</span>
              </td>
              <td class="px-6 py-4">
                <span
                  class="pill"
                  :class="entryState(p).cls"
                  :title="entryTitle(p)"
                >
                  <font-awesome-icon
                    v-if="entryState(p).warn"
                    icon="exclamation-triangle"
                    class="text-xs"
                  />
                  {{ entryState(p).label }}
                </span>
              </td>
              <td class="px-6 py-4 text-ink-soft">
                {{ moment(p.created_at).format('DD.MM.YYYY HH:mm') }}
              </td>
              <td class="px-6 py-4 text-right">
                <button
                  class="btn btn-secondary btn-sm"
                  @click="selectPatient(p.patient_id)"
                >
                  <span>Correct Data</span>
                  <font-awesome-icon icon="arrow-right" class="text-xs" />
                </button>
              </td>
            </tr>
            <tr v-if="filteredPatients.length === 0">
              <td colspan="5" class="text-center py-8 text-ink-muted">
                No patients match the search criteria.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Detail/Correction Navigation View -->
    <div v-else class="space-y-6">
      <div class="card card-pad">
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div class="space-y-2">
            <button class="btn btn-secondary btn-sm" @click="backToList">
              <font-awesome-icon icon="arrow-left" />
              <span>Back to Patient List</span>
            </button>
            <div class="flex items-center gap-3 pt-2">
              <h1 class="page-title mb-0">Correction Panel</h1>
              <span class="pill pill-brand">
                <font-awesome-icon icon="user" />
                {{ selectedPatientId }}
              </span>
            </div>
          </div>

          <!-- Study Completion Status Guarded by Audit Trail -->
          <div v-if="selectedPatient" class="flex items-center gap-3">
            <span class="text-sm font-semibold text-ink-soft"
              >Study Completed:</span
            >
            <button
              class="toggle-track rounded-full transition-colors duration-200 relative focus:outline-none p-0 border-0 flex items-center cursor-pointer"
              :class="{ 'track-active': selectedPatient.study_completed }"
              @click="toggleStudyCompleted"
            >
              <div
                class="toggle-knob rounded-full bg-white shadow-sm"
                :class="{ 'knob-active': selectedPatient.study_completed }"
              ></div>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading Details -->
      <div
        v-if="loadingDetails"
        class="flex items-center gap-2 text-ink-muted py-12 justify-center card card-pad"
      >
        <font-awesome-icon icon="circle-notch" spin class="text-brand-600" />
        <span>Loading patient forms…</span>
      </div>

      <!-- Patient Forms List grouped by Visit -->
      <div v-else class="space-y-6">
        <div v-for="visit in visits" :key="visit.number" class="card card-pad">
          <div class="flex items-center gap-3 mb-5">
            <span
              class="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center text-sm font-bold"
              >{{ visit.number }}</span
            >
            <h2 class="section-title">Visit {{ visit.number }}</h2>
          </div>

          <!-- Grid of Clinical Forms -->
          <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <router-link
              v-for="f in visit.forms"
              :key="f.key"
              :to="formRoute(f)"
              class="form-card card card-hover p-5 flex flex-col justify-between min-h-[140px] group"
            >
              <h3
                class="form-card-title text-base font-bold text-ink leading-tight"
              >
                {{ f.title }}
              </h3>
              <div class="mt-4 flex items-center justify-between">
                <span class="pill" :class="statusPill(f.status)">
                  <span class="status-dot" :class="statusDot(f.status)"></span>
                  {{ statusLabel(f.status) }}
                </span>
                <span
                  class="text-xs font-semibold text-brand-600 hover:underline"
                >
                  Correct <font-awesome-icon icon="edit" class="ml-0.5" />
                </span>
              </div>
            </router-link>
          </div>
        </div>

        <div v-if="visits.length === 0" class="card card-pad text-ink-muted">
          No forms found for this patient.
        </div>

        <!-- Danger Zone: permanently delete this study subject (admins only).
             Collapsed by default so it cannot be triggered by mistake. -->
        <div
          v-if="isAdmin && selectedPatient"
          class="rounded-2xl border border-red-200 bg-red-50/40 overflow-hidden"
        >
          <button
            class="w-full flex items-center justify-between gap-3 px-6 py-4 text-left focus:outline-none cursor-pointer"
            @click="dangerOpen = !dangerOpen"
          >
            <span class="flex items-center gap-3">
              <span
                class="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center"
              >
                <font-awesome-icon icon="exclamation-triangle" />
              </span>
              <span>
                <span class="block text-base font-bold text-red-700"
                  >Danger Zone</span
                >
                <span class="block text-xs text-red-500"
                  >Irreversible actions for this study subject</span
                >
              </span>
            </span>
            <font-awesome-icon
              icon="chevron-right"
              class="text-red-400 transform transition-transform duration-200"
              :class="{ 'rotate-90': dangerOpen }"
            />
          </button>

          <div v-if="dangerOpen" class="px-6 pb-6 pt-4 border-t border-red-100">
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            >
              <div class="text-sm text-ink-soft max-w-xl">
                <p class="font-semibold text-red-700 mb-1">
                  Delete this study subject
                </p>
                <p>
                  Permanently removes patient
                  <span class="font-semibold text-ink">{{
                    selectedPatientId
                  }}</span>
                  and all
                  <span class="font-semibold text-ink">{{
                    selectedPatientForms.length
                  }}</span>
                  associated form{{
                    selectedPatientForms.length === 1 ? '' : 's'
                  }}. This cannot be undone. The deletion is recorded in the
                  audit log.
                </p>
              </div>
              <button
                class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors focus:outline-none cursor-pointer shrink-0"
                @click="openDeleteModal"
              >
                <font-awesome-icon icon="trash" />
                <span>Delete Subject</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm study completed change modal -->
    <ConfirmChangesModal
      v-if="selectedPatient"
      :is-open="showCompletedModal"
      :changes="completedChanges"
      require-reason
      @confirm="submitCompletedCorrection"
      @cancel="showCompletedModal = false"
    />

    <!-- Delete confirmation banner: guarded by a Patient-ID retype + reason. -->
    <transition name="fade">
      <div
        v-if="showDeleteModal && selectedPatient"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
        @click.self="closeDeleteModal"
      >
        <div
          class="bg-white rounded-2xl shadow-xl max-w-lg w-full flex flex-col overflow-hidden border border-gray-100 rise-in"
        >
          <!-- Warning banner -->
          <div class="bg-red-600 text-white px-6 py-4 flex items-center gap-3">
            <font-awesome-icon icon="exclamation-triangle" class="text-xl" />
            <div>
              <h3 class="text-base font-bold leading-tight">
                Permanently delete study subject
              </h3>
              <p class="text-xs text-red-100">This action cannot be undone.</p>
            </div>
          </div>

          <div class="p-6 space-y-4">
            <p class="text-sm text-ink-soft">
              You are about to permanently delete patient
              <span class="font-bold text-ink">{{ selectedPatientId }}</span>
              and all
              <span class="font-bold text-ink">{{
                selectedPatientForms.length
              }}</span>
              of its form{{ selectedPatientForms.length === 1 ? '' : 's' }}.
              This removes the data from the database and cannot be reversed.
            </p>

            <div>
              <label
                class="block text-xs font-bold text-ink-muted uppercase tracking-wider mb-1"
              >
                Type the Patient ID to confirm
              </label>
              <input
                v-model="deleteConfirmId"
                type="text"
                :placeholder="selectedPatientId"
                autocomplete="off"
                class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-red-500 focus:ring focus:ring-red-100 focus:outline-none text-sm text-ink transition-colors"
              />
            </div>

            <div>
              <label
                class="block text-xs font-bold text-ink-muted uppercase tracking-wider mb-1"
              >
                Reason
                <span class="normal-case font-normal"
                  >(recorded in the audit log)</span
                >
              </label>
              <textarea
                v-model="deleteReason"
                rows="2"
                placeholder="Why is this subject being deleted?"
                class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-red-500 focus:ring focus:ring-red-100 focus:outline-none text-sm text-ink transition-colors resize-none"
              ></textarea>
            </div>

            <p v-if="deleteError" class="text-sm text-red-600">
              {{ deleteError }}
            </p>
          </div>

          <div
            class="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50"
          >
            <button
              class="btn btn-secondary btn-md"
              :disabled="deleting"
              @click="closeDeleteModal"
            >
              Cancel
            </button>
            <button
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors focus:outline-none"
              :class="
                canDelete
                  ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
                  : 'bg-red-300 cursor-not-allowed'
              "
              :disabled="!canDelete"
              @click="confirmDelete"
            >
              <font-awesome-icon v-if="deleting" icon="circle-notch" spin />
              <font-awesome-icon v-else icon="trash" />
              <span>{{ deleting ? 'Deleting…' : 'Delete permanently' }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { rest, functions } from 'helpers/api'
import ConfirmChangesModal from 'components/ConfirmChangesModal.vue'

export default {
  name: 'DataManagement',
  components: {
    ConfirmChangesModal,
  },
  data() {
    return {
      patients: [],
      projects: ['all', 'bex', 'bro', 'trx'],
      selectedProject: 'all',
      searchQuery: '',
      // Derived entry-status filter: all | active | entriesComplete | completed
      statusFilter: 'all',
      statusFilters: [
        { key: 'all', label: 'All' },
        { key: 'active', label: 'Active' },
        { key: 'entriesComplete', label: 'Entries Complete' },
        { key: 'completed', label: 'Completed' },
      ],
      loading: true,
      error: null,

      // Detail navigation state
      selectedPatientId: null,
      selectedPatient: null,
      selectedPatientForms: [],
      loadingDetails: false,

      // Toggle study completion state
      showCompletedModal: false,
      savingCompleted: false,

      // Danger Zone / delete-subject state
      dangerOpen: false,
      showDeleteModal: false,
      deleteConfirmId: '',
      deleteReason: '',
      deleting: false,
      deleteError: null,
    }
  },
  computed: {
    filteredPatients() {
      return this.patients.filter(p => {
        // Project slug filter
        if (
          this.selectedProject !== 'all' &&
          (p.project_id || '').toLowerCase() !== this.selectedProject
        ) {
          return false
        }
        // Derived entry-status filter. "completed" covers both the clean and the
        // mismatch (⚠) completed states, since both are study_completed=true.
        if (this.statusFilter !== 'all') {
          const code = this.entryState(p).code
          if (this.statusFilter === 'completed') {
            if (code !== 'completed' && code !== 'completedIncomplete') {
              return false
            }
          } else if (code !== this.statusFilter) {
            return false
          }
        }
        // Search query filter (Patient ID)
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase()
          return (p.patient_id || '').toLowerCase().includes(q)
        }
        return true
      })
    },
    visits() {
      const groups = {}
      const ordered = [...this.selectedPatientForms].sort((a, b) =>
        a.key.localeCompare(b.key, undefined, { numeric: true })
      )
      ordered.forEach(f => {
        const visitNum = (f.key.match(/(\d+)/) || [])[1] || '1'
        if (!groups[visitNum]) {
          groups[visitNum] = []
        }
        groups[visitNum].push(f)
      })
      return Object.keys(groups)
        .sort((a, b) => Number(a) - Number(b))
        .map(num => ({
          number: num,
          forms: groups[num],
        }))
    },
    completedChanges() {
      if (!this.selectedPatient) return []
      return [
        {
          key: 'study_completed',
          label: 'Study data entry completed',
          oldValue: this.selectedPatient.study_completed,
          newValue: !this.selectedPatient.study_completed,
        },
      ]
    },
    isAdmin() {
      return this.$store.getters['auth/isAdmin'] === true
    },
    // The delete is armed only when the admin retypes the exact Patient ID and
    // gives a reason — and never while a delete is already in flight.
    canDelete() {
      return (
        !this.deleting &&
        this.deleteConfirmId.trim() === (this.selectedPatientId || '') &&
        this.deleteReason.trim().length > 0
      )
    },
  },
  created() {
    this.fetchPatients()
  },
  methods: {
    async fetchPatients() {
      this.loading = true
      this.error = null
      try {
        // Load the patient list and the derived entry-completeness view together,
        // then attach `entries_complete` (+ counts) to each patient. If the view
        // call fails we still show the list — completeness just falls back to
        // "unknown" (treated as not complete) rather than breaking the page.
        const [patients, entryStatus] = await Promise.all([
          rest.listAllPatients(),
          rest.listPatientEntryStatus().catch(() => []),
        ])
        const statusById = new Map(
          (entryStatus || []).map(s => [s.patient_id, s])
        )
        this.patients = patients.map(p => {
          const s = statusById.get(p.patient_id)
          return {
            ...p,
            entries_complete: !!(s && s.entries_complete),
            entry_forms_total: s ? s.entry_forms_total : 0,
            entry_forms_finished: s ? s.entry_forms_finished : 0,
          }
        })
      } catch (e) {
        this.error = 'Failed to load patients list.'
      } finally {
        this.loading = false
      }
    },
    async selectPatient(patientId) {
      this.loadingDetails = true
      this.selectedPatientId = patientId
      try {
        const [patient, forms] = await Promise.all([
          rest.getPatient(patientId),
          rest.listForms(patientId),
        ])
        this.selectedPatient = patient
        this.selectedPatientForms = forms
      } catch (e) {
        this.error = 'Failed to load patient details.'
      } finally {
        this.loadingDetails = false
      }
    },
    backToList() {
      this.selectedPatientId = null
      this.selectedPatient = null
      this.selectedPatientForms = []
      this.dangerOpen = false
      this.showDeleteModal = false
    },
    openDeleteModal() {
      this.deleteConfirmId = ''
      this.deleteReason = ''
      this.deleteError = null
      this.showDeleteModal = true
    },
    closeDeleteModal() {
      if (this.deleting) return
      this.showDeleteModal = false
    },
    async confirmDelete() {
      if (!this.canDelete) return
      this.deleting = true
      this.deleteError = null
      try {
        await functions.deletePatient({
          patientId: this.selectedPatientId,
          reason: this.deleteReason.trim(),
        })
        this.showDeleteModal = false
        this.backToList()
        await this.fetchPatients()
      } catch (e) {
        const data = e.response && e.response.data
        this.deleteError =
          (data && ((data.error && data.error.message) || data.message)) ||
          e.message ||
          'Failed to delete the study subject.'
      } finally {
        this.deleting = false
      }
    },
    toggleStudyCompleted() {
      if (!this.selectedPatient || this.savingCompleted) return
      this.showCompletedModal = true
    },
    async submitCompletedCorrection(reason) {
      this.showCompletedModal = false
      this.savingCompleted = true
      try {
        const updated = await functions.correctPatient({
          patientId: this.selectedPatientId,
          patch: { study_completed: !this.selectedPatient.study_completed },
          reason,
        })
        if (updated) {
          this.selectedPatient = updated
        }
        // Refresh patients list so it updates in the background
        await this.fetchPatients()
      } catch (e) {
        alert('Failed to update study completion: ' + e.message)
      } finally {
        this.savingCompleted = false
      }
    },
    // Derived subject status from the manual study_completed flag crossed with the
    // computed entries_complete (all Screening + Patient Data forms finished):
    //   Active            – data entry still open, not checked out
    //   Entries Complete  – every entry form filled, but not yet checked out
    //   Completed         – checked out, and entries are indeed all filled
    //   Completed ⚠       – checked out but entries are NOT all filled (mismatch)
    entryState(p) {
      const done = !!p.entries_complete
      if (p.study_completed) {
        return done
          ? { code: 'completed', label: 'Completed', cls: 'pill-green' }
          : {
              code: 'completedIncomplete',
              label: 'Completed',
              cls: 'pill-warn',
              warn: true,
            }
      }
      return done
        ? {
            code: 'entriesComplete',
            label: 'Entries Complete',
            cls: 'pill-amber',
          }
        : { code: 'active', label: 'Active', cls: 'pill-muted' }
    },
    entryTitle(p) {
      const t = p.entry_forms_total || 0
      const f = p.entry_forms_finished || 0
      const base = `${f} of ${t} entry form${
        t === 1 ? '' : 's'
      } complete (Screening + Patient Data)`
      if (p.study_completed && !p.entries_complete) {
        return `${base} — marked study-completed while entries are still incomplete`
      }
      return base
    },
    statusPill(s) {
      if (s === 'finished') return 'pill-green'
      if (s === 'inProgress') return 'pill-amber'
      return 'pill-muted'
    },
    statusDot(s) {
      if (s === 'finished') return 'dot-green'
      if (s === 'inProgress') return 'dot-amber'
      return 'dot-gray'
    },
    statusLabel(s) {
      if (s === 'finished') return 'Completed'
      if (s === 'inProgress') return 'In progress'
      return 'Not started'
    },
    formRoute(f) {
      const params = {
        projectSlug: (this.selectedPatient.project_id || '').toLowerCase(),
        patientId: this.selectedPatientId,
      }
      const visitNumber = (f.key.match(/(\d+)/) || [])[1] || '1'
      const query = { mode: 'correct' }

      if (f.key.startsWith('screening')) {
        return { name: 'Screening', params: { ...params, visitNumber }, query }
      }
      if (f.key.endsWith('-data')) {
        return { name: 'Upload', params: { ...params, visitNumber }, query }
      }
      return { name: 'Visit', params: { ...params, visitNumber }, query }
    },
  },
}
</script>

<style scoped>
.form-card:hover .form-card-title {
  color: #831f82;
}
/* Mismatch badge: marked study-completed while entries are still incomplete. */
.pill-warn {
  background-color: #fef2f2;
  color: #b91c1c;
}
.dot-green {
  background-color: #16a34a;
}
.dot-amber {
  background-color: #d97706;
}
.dot-gray {
  background-color: #9ca3af;
}
.toggle-track {
  width: 2.75rem;
  height: 1.5rem;
  background-color: #e5e7eb;
  padding: 0.125rem;
}
.track-active {
  background-color: #831f82;
}
.toggle-knob {
  width: 1.25rem;
  height: 1.25rem;
  transition: transform 0.2s ease-in-out;
}
.knob-active {
  transform: translateX(1.25rem);
}

/* Delete-confirmation modal transition (self-contained; mirrors ConfirmChangesModal). */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.rise-in {
  animation: riseIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes riseIn {
  from {
    transform: translateY(12px) scale(0.98);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}
</style>
